import { motion } from 'framer-motion';
import { ShoppingCart, Check } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

export default function ProductCard({ product, index = 0 }) {
  const { t, getLocalizedField } = useLanguage();
  const { addItem } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  
  const handleAddToCart = () => {
    setIsAdding(true);
    addItem(product);
    toast.success(t('added'), {
      description: getLocalizedField(product, 'name'),
    });
    setTimeout(() => setIsAdding(false), 1000);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        <img
          src={product.image_url}
          alt={getLocalizedField(product, 'name')}
          className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        {/* Category Badge */}
        <span className="absolute top-3 left-3 px-3 py-1.5 bg-primary/90 backdrop-blur-sm rounded-full text-xs font-semibold uppercase tracking-wider text-white shadow-lg">
          {product.category}
        </span>
        
        {/* Stock Badge */}
        {product.stock === 0 && (
          <div className="absolute top-3 right-3 px-3 py-1.5 bg-red-600 backdrop-blur-sm rounded-full text-xs font-semibold uppercase tracking-wider text-white shadow-lg">
            Rupture de stock
          </div>
        )}
        {product.stock > 0 && product.stock <= 10 && (
          <div className="absolute top-3 right-3 px-3 py-1.5 bg-orange-500 backdrop-blur-sm rounded-full text-xs font-semibold uppercase tracking-wider text-white shadow-lg">
            Stock faible
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-5 lg:p-6">
        <h3 className="font-bold text-lg text-text_primary mb-2 line-clamp-2 leading-snug" data-testid={`product-name-${product.id}`}>
          {getLocalizedField(product, 'name')}
        </h3>
        <p className="text-sm text-text_secondary mb-4 line-clamp-2 leading-relaxed">
          {getLocalizedField(product, 'description')}
        </p>
        
        <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
          <div className="flex-1">
            <p className="text-xs text-text_secondary mb-1">Prix</p>
            <span className="text-2xl font-bold text-primary" data-testid={`product-price-${product.id}`}>
              €{product.price.toFixed(2)}
            </span>
            
            {/* Stock Indicator */}
            <div className="mt-2">
              {product.stock === 0 ? (
                <p className="text-xs font-semibold text-red-600">
                  ❌ Rupture de stock
                </p>
              ) : product.stock <= 10 ? (
                <p className="text-xs font-semibold text-orange-600">
                  ⚠️ Stock faible: {product.stock} restants
                </p>
              ) : (
                <p className="text-xs font-semibold text-green-600">
                  ✓ En stock: {product.stock} disponibles
                </p>
              )}
            </div>
          </div>
          
          <button
            onClick={handleAddToCart}
            disabled={product.stock <= 0 || isAdding}
            className={`flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all duration-300 shadow-md ${
              product.stock <= 0 
                ? 'bg-red-100 text-red-600 cursor-not-allowed border-2 border-red-300'
                : isAdding
                  ? 'bg-green-500 text-white'
                  : 'bg-primary text-white hover:bg-primary_hover hover:scale-105 hover:shadow-lg'
            }`}
            data-testid={`add-to-cart-${product.id}`}
          >
            {isAdding ? (
              <>
                <Check className="w-5 h-5" />
                <span>{t('added')}</span>
              </>
            ) : product.stock <= 0 ? (
              <span>Rupture</span>
            ) : (
              <>
                <ShoppingCart className="w-5 h-5" />
                <span>{t('addToCart')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
